import { create } from "zustand";
import type {
  ArcGenerationState,
  ArcTransportFrame,
  ArcInteractionSpec,
  ArcInteractionSubmission,
} from "../types/arcGenerationSession";
import { appendAtPath, setAtPath } from "./_frameHelpers";

interface ArcGenerationSessionStore {
  sessionState: ArcGenerationState | null;
  isStreaming: boolean;
  // Slot keys of stream_list fields whose ``list_done`` frame has
  // arrived for the interaction currently streaming.  Lets the UI
  // act on a list being fully present (e.g. enable "Start" once
  // ``heroMilestones`` is done) before the whole envelope
  // finishes.  Reset on every ``state_snapshot`` (new interaction).
  completedLists: string[];
  activeRequestId: string | null;
  error: { code: string; message: string } | null;

  processFrame: (frame: ArcTransportFrame) => void;
  setActiveRequestId: (id: string | null) => void;
  /**
   * Bail out of the current streaming state with an error.  Called
   * when the WebSocket request ends in a non-success terminal state
   * without any framing telling us so (timeout, ws error, etc.).
   */
  abortStreamingWithReason: (reason: string) => void;
  /**
   * Write an optimistic submission onto an interaction (local-only).
   * The server echo overwrites with the canonical submission produced
   * by the kind's ``format_submission``.
   */
  setPendingSubmission: (
    interactionId: string,
    partial: { eventId: string; displayText: string },
  ) => void;
  reset: () => void;
}

export const useArcGenerationSessionStore = create<ArcGenerationSessionStore>(
  (set, get) => ({
    sessionState: null,
    isStreaming: false,
    completedLists: [],
    activeRequestId: null,
    error: null,

    processFrame: (frame: ArcTransportFrame) => {
      switch (frame.kind) {
        case "state_snapshot": {
          set({
            sessionState: frame.state,
            isStreaming: true,
            // New interaction streaming — clear per-interaction
            // list-completion tracking.
            completedLists: [],
            error: null,
          });
          break;
        }
        case "block_delta": {
          const state = get().sessionState;
          if (!state) break;
          const newState = structuredClone(state);
          applyBlockDelta(newState, frame.slotKey, frame.value);
          set({ sessionState: newState });
          break;
        }
        case "text_delta": {
          const state = get().sessionState;
          if (!state) break;
          const newState = structuredClone(state);
          applyTextDelta(newState, frame.slotKey, frame.delta);
          set({ sessionState: newState });
          break;
        }
        case "text_done": {
          break;
        }
        case "list_item_open": {
          const state = get().sessionState;
          if (!state) break;
          const newState = structuredClone(state);
          const interaction = getActiveInteraction(newState);
          if (interaction) {
            appendListItemShell(interaction, frame.slotKey, frame.itemId);
          }
          set({ sessionState: newState });
          break;
        }
        case "list_item_close": {
          break;
        }
        case "list_done": {
          const prior = get().completedLists;
          if (!prior.includes(frame.slotKey)) {
            set({ completedLists: [...prior, frame.slotKey] });
          }
          break;
        }
        case "content_done": {
          set({ isStreaming: false });
          break;
        }
        case "run_done": {
          set({ activeRequestId: null, isStreaming: false });
          break;
        }
        case "error": {
          // Clear any optimistic pending-submission flag on the
          // currently-active interaction so the "Sending your
          // answer…" indicator doesn't get stuck if the backend
          // failed before it could echo the canonical submission.
          // Without this, a mid-stream error (e.g. truncated LLM
          // response) leaves the UI looking like the request is
          // still in flight even though isStreaming is now false.
          const state = get().sessionState;
          let nextState = state;
          if (state) {
            const scope = state.scopes[state.activeScopeId];
            const activeId = scope?.work?.activeInteractionId;
            const interaction = activeId
              ? scope?.work?.interactions?.[activeId]
              : null;
            const submission = interaction?.submission as
              | ArcInteractionSubmission
              | null
              | undefined;
            if (submission?.pending) {
              nextState = structuredClone(state);
              const scope2 = nextState.scopes[nextState.activeScopeId];
              const it = scope2.work.interactions[activeId!];
              const sub = it.submission as ArcInteractionSubmission;
              it.submission = { ...sub, pending: false };
            }
          }
          set({
            sessionState: nextState,
            error: { code: frame.code, message: frame.message },
            isStreaming: false,
          });
          break;
        }
      }
    },

    setActiveRequestId: (id) => set({ activeRequestId: id }),

    /**
     * Forcibly clear the streaming state when the WebSocket request
     * ends without ever delivering a terminal frame (timeout, ws
     * error before any tokens, server crash mid-LLM-call).  Without
     * this, the "Sending your answer…" spinner gets stuck because
     * neither ``run_done`` / ``error`` / ``content_done`` ever
     * cleared ``isStreaming`` or the optimistic ``pending``
     * submission flag.
     *
     * Safe to call multiple times; idempotent.  Records an error
     * with the supplied reason so the UI can surface a retry path
     * instead of looking like the request is still in flight.
     */
    abortStreamingWithReason: (reason: string) => {
      const state = get().sessionState;
      let nextState = state;
      if (state) {
        const scope = state.scopes[state.activeScopeId];
        const activeId = scope?.work?.activeInteractionId;
        const interaction = activeId
          ? scope?.work?.interactions?.[activeId]
          : null;
        const submission = interaction?.submission as
          | ArcInteractionSubmission
          | null
          | undefined;
        if (submission?.pending) {
          nextState = structuredClone(state);
          const scope2 = nextState.scopes[nextState.activeScopeId];
          const it = scope2.work.interactions[activeId!];
          const sub = it.submission as ArcInteractionSubmission;
          it.submission = { ...sub, pending: false };
        }
      }
      set({
        sessionState: nextState,
        isStreaming: false,
        activeRequestId: null,
        error: { code: "stream_terminated", message: reason },
      });
    },

    setPendingSubmission: (interactionId, partial) => {
      const state = get().sessionState;
      if (!state) return;
      const newState = structuredClone(state);
      const scope = newState.scopes[newState.activeScopeId];
      const interaction = scope?.work?.interactions?.[interactionId];
      if (!interaction) return;
      const submission: ArcInteractionSubmission = {
        ...((interaction.submission as ArcInteractionSubmission | null | undefined) ?? {}),
        eventId: partial.eventId,
        displayText: partial.displayText,
        pending: true,
      };
      interaction.submission = submission;
      set({ sessionState: newState });
    },

    reset: () =>
      set({
        sessionState: null,
        isStreaming: false,
        completedLists: [],
        activeRequestId: null,
        error: null,
      }),
  })
);

/** Return the active interaction (or null) on the active scope. */
function getActiveInteraction(state: ArcGenerationState): ArcInteractionSpec | null {
  const scope = state.scopes[state.activeScopeId];
  if (!scope) return null;
  const activity = scope.work;
  const id = activity.activeInteractionId;
  if (!id) return null;
  return activity.interactions[id] ?? null;
}

function applyBlockDelta(
  state: ArcGenerationState,
  slotKey: string,
  value: unknown
): void {
  const interaction = getActiveInteraction(state);
  if (!interaction) return;
  if (slotKey.includes(".")) {
    setAtPath(interaction as unknown as Record<string, unknown>, slotKey, value);
    return;
  }
  (interaction as unknown as Record<string, unknown>)[slotKey] = value;
}

function applyTextDelta(
  state: ArcGenerationState,
  slotKey: string,
  delta: string
): void {
  const interaction = getActiveInteraction(state);
  if (!interaction) return;

  if (slotKey.includes(".")) {
    appendAtPath(interaction as unknown as Record<string, unknown>, slotKey, delta);
    return;
  }

  const rec = interaction as unknown as Record<string, unknown>;
  const current = (rec[slotKey] as string | undefined) || "";
  rec[slotKey] = current + delta;
}

/** Append an empty list-item shell for *slotKey* on the active interaction.
 *
 * The shell shape is derived generically from the prelude's contentFields
 * — stream subfields default to "", block subfields to null, and the
 * server-assigned id lands on the uniform "itemId" field.  Adding a new
 * StreamList kind requires no change here.
 */
function appendListItemShell(
  interaction: ArcInteractionSpec,
  slotKey: string,
  itemId: string
): void {
  // The prelude's contentFields are carried as part of the interaction's
  // staticShape / prelude data.  We look for the matching stream_list
  // entry's subFields.
  const subFields = findSubFieldsForSlot(interaction, slotKey);
  const shell: Record<string, unknown> = { itemId };
  for (const [name, ftype] of subFields) {
    shell[name] = ftype === "stream" ? "" : null;
  }
  const rec = interaction as unknown as Record<string, unknown>;
  const list = (rec[slotKey] as unknown[] | undefined) ?? [];
  list.push(shell);
  rec[slotKey] = list;
}

/** Walk the interaction's contentFields (stashed at materialize time)
 * to find the stream_list entry for *slotKey* and return its subFields. */
function findSubFieldsForSlot(
  interaction: ArcInteractionSpec,
  slotKey: string
): Array<[string, string]> {
  const rec = interaction as unknown as Record<string, unknown>;
  const contentFields = rec.contentFields as Array<Record<string, unknown>> | undefined;
  if (!contentFields) return [];
  for (const entry of contentFields) {
    if (entry.key === slotKey && entry.type === "stream_list") {
      return (entry.subFields as Array<[string, string]> | undefined) ?? [];
    }
  }
  return [];
}

// Convenience selectors
export const useArcGenState = () =>
  useArcGenerationSessionStore((s) => s.sessionState);
export const useArcGenIsStreaming = () =>
  useArcGenerationSessionStore((s) => s.isStreaming);
export const useArcGenCompletedLists = () =>
  useArcGenerationSessionStore((s) => s.completedLists);
export const useArcGenError = () =>
  useArcGenerationSessionStore((s) => s.error);

